import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowRight, Users, FileText, Phone, Droplets, BarChart3, Search, FileCheck, Calendar, MapPin, ExternalLink, TrendingUp, Briefcase } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Badge } from './ui/badge';
import { useEffect, useState } from "react";
import { getPublicProjects, ProjectPayload } from "../services/Project";
import { getPublicDocument, getDocumentDownloadUrl, INSTITUTIONAL_PROFILE_KEY } from "../services/Document";
import { SEOHead } from './SEOHead';
import { useSiteSettings, safeUrl, DEFAULT_SITE_SETTINGS, getPageSeo, buildFaqSchema } from '../services/SiteSettings';
import { buildOrganizationSchema } from '../data/organizationSchema.mjs';
import { PageFaq } from './PageAeo';
import { htmlToText } from '../services/richText';

interface HomePageProps {
  onPageChange: (page: string) => void;
}

export function HomePage({ onPageChange }: HomePageProps) {
  const siteSettings = useSiteSettings();
  const { hero, about, home } = siteSettings;
  const seo = getPageSeo(siteSettings, '/');
  const faqSchema = buildFaqSchema(seo.faqs);
  const [allProjects, setAllProjects] = useState<ProjectPayload[]>([]);
  const [loading, setLoading] = useState(true);
  const [profilePdfUrl, setProfilePdfUrl] = useState("/USP Consultacy Institutional Profile 2025.pdf");

  useEffect(() => {
    getPublicDocument(INSTITUTIONAL_PROFILE_KEY)
      .then((doc) => {
        if (doc?.fileName) setProfilePdfUrl(getDocumentDownloadUrl(doc.fileName));
      })
      .catch(() => {
        // Keep bundled PDF as fallback
      });
  }, []);

  const partners = (home.partners || []).filter((p) => safeUrl(p.src));

  const organizationSchema = buildOrganizationSchema(siteSettings);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://uspconsultancy.com.np/"
      }
    ]
  };

  useEffect(() => {
    fetchRecentProjects();
  }, []);

  const fetchRecentProjects = async () => {
    // Show the cached list right away, then always refresh so newly added projects appear
    const storedProjects = sessionStorage.getItem('publicProjects');
    if (storedProjects) {
      setAllProjects(JSON.parse(storedProjects));
    } else {
      setLoading(true);
    }

    try {
      const allProjects: ProjectPayload[] = await getPublicProjects();
      sessionStorage.setItem('publicProjects', JSON.stringify(allProjects));
      setAllProjects(allProjects);
    } catch (err) {
      console.error('Failed to load recent projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const sortProjects = (data: ProjectPayload[]) => {
    return data.sort((a, b) => {
      // Extract year from text format like "March-April 2025"
      const extractYear = (yearText: string | undefined): number => {
        if (!yearText) return 0;

        // Try to find 4-digit year in the text
        const yearMatch = yearText.match(/\b(20\d{2})\b/);
        if (yearMatch) {
          return parseInt(yearMatch[1], 10);
        }

        // If no 4-digit year found, try to extract any numbers
        const anyYearMatch = yearText.match(/\b(\d{4})\b/);
        if (anyYearMatch) {
          return parseInt(anyYearMatch[1], 10);
        }

        return 0; // Return 0 if no year found
      };

      const yearA = extractYear(a.year);
      const yearB = extractYear(b.year);

      // Handle cases where year might be missing or invalid
      if (yearA === 0 && yearB === 0) return 0;
      if (yearA === 0) return 1; // projects without year go to end
      if (yearB === 0) return -1; // projects with year come first

      return yearB - yearA; // descending order (2025 -> 2024 -> 2023)
    });
  };

  // Admin-picked projects; falls back to the latest 3 when none are chosen or found.
  const featuredProjects = (home.featuredProjectIds || [])
    .map((id) => allProjects.find((p: any) => Number(p.id) === Number(id)))
    .filter((p): p is ProjectPayload => p !== undefined)
    .slice(0, 3);
  const recentProjects = featuredProjects.length > 0
    ? featuredProjects
    : sortProjects([...allProjects]).slice(0, 3);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Water Resources': return 'bg-blue-500';
      case 'Social Research': return 'bg-purple-500';
      case 'Environmental Assessment': return 'bg-green-500';
      case 'Data Collection': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const truncateText = (text: string, length: number) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.slice(0, length) + '...';
  };

  const heroImages = hero.images.map(safeUrl).filter(Boolean);
  const images = heroImages.length > 0 ? heroImages : DEFAULT_SITE_SETTINGS.hero.images;

  const accomplishmentImages = (home.accomplishments || []).filter((img) => safeUrl(img.src)).slice(0, 3);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={seo.title || hero.title}
        description={seo.description}
        canonical="https://uspconsultancy.com.np/"
        keywords={seo.keywords}
        structuredData={faqSchema ? [organizationSchema, breadcrumbSchema, faqSchema] : [organizationSchema, breadcrumbSchema]}
      />

      {/* Hero Banner */}
      <section
        className="relative w-full flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700"
        style={{ minHeight: "110vh" }}
      >        <div className="absolute inset-0 bg-black/40"></div>
        <ImageWithFallback
          src={images[currentIndex % images.length]}
          alt="USP Consultancy - Professional consulting services in socio-economic research and development"
          className="absolute inset-0 w-full h-full object-contain"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/50"></div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl mb-6">{hero.title}</h1>
          <p className="text-xl mb-8 text-gray-200">
            {hero.tagline}
          </p>
          <Button
            size="lg"
            onClick={() => onPageChange('contact')}
            className="bg-white text-black hover:bg-gray-100 cursor-pointer"
          >
            Get in Touch
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Intro Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-content">
            <h2>About {siteSettings.contact.name}</h2>


            {about.homeIntro.map((para, i) => (
              <p key={i} className={i === about.homeIntro.length - 1 ? 'last-para' : undefined}>
                {para}
              </p>
            ))}

            <div className="button-wrap">
              <a
                href={profilePdfUrl}
                download="USP Consultacy Institutional Profile.pdf"
                className="profile-btn"
              >
                See Institutional Profile PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="mb-4">Recently Completed Projects</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore some of our recently completed and ongoing projects that demonstrate our expertise and impact across various sectors.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading recent projects...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                {recentProjects.map((project, index) => (
                  <Card
                    key={index}
                    className="overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="relative w-full h-full overflow-hidden">
                      {/* Show image only if imageUrl exists */}
                      {project.imageUrl && (
                        <ImageWithFallback
                          src={project.imageUrl}
                          alt={project.title}
                          className="w-full h-full object-contain"
                        />
                      )}
                      <div className="absolute top-4 left-4">
                        <div
                          className={`w-3 h-3 rounded-full ${getCategoryColor(
                            project.category
                          )}`}
                        ></div>
                      </div>
                      {/* <div className="absolute top-4 right-4">
                        <Badge className={getStatusColor(project.status)}>
                          {project.status}
                        </Badge>
                      </div> */}
                    </div>

                    <CardHeader>
                      <CardTitle className="text-lg leading-tight">
                        {project.title}
                      </CardTitle>
                      <div className="flex flex-col space-y-2 text-sm text-gray-500 mt-1">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{project.year}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MapPin className="w-4 h-4" />
                          <span>{project.location}</span>
                        </div>
                        {/* Type of Assignment - New Section */}
                        {project.typeOfAssignment && (
                          <div className="flex items-center space-x-1">
                            <Briefcase className="w-4 h-4" />
                            <span className="text-xs">{project.typeOfAssignment}</span>
                          </div>
                        )}
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600 mb-4 text-sm">
                        {truncateText(htmlToText(project.description), 120)}
                      </p>

                      <div className="mb-4 flex flex-wrap gap-2">
                        <Badge variant="secondary" className="mb-2">
                          {project.category}
                        </Badge>
                        {/* Type of Assignment Badge - Alternative display */}
                        {project.typeOfAssignment && (
                          <Badge variant="outline" className="text-xs">
                            {project.typeOfAssignment}
                          </Badge>
                        )}
                      </div>

                      {project.highlights?.length > 0 && (
                        <div className="space-y-2 mb-4">
                          <h4 className="text-sm">Key Highlights:</h4>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {project.highlights.map((highlight, idx) => (
                              <li
                                key={idx}
                                className="flex items-start space-x-2"
                              >
                                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* View All Projects Button */}
              <div className="text-center">
                <Button
                  size="lg"
                  onClick={() => onPageChange('projects')}
                  className="cursor-pointer"
                >
                  View All Projects
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Service Areas Image */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-2 text-center">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Service Areas</h3>
                <div className="w-full h-96 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                  <ImageWithFallback
                    src="/services-areas.png"
                    alt="USP Consultancy Service Areas - Climate Resilience, Food Security, Poverty Alleviation, Governance, Digital Development, Economic Development, Social Protection, Education"
                    className="w-full h-full object-contain"
                    fallbackSrc="/placeholder-services.png"
                  />
                </div>
                <p className="text-gray-600 mt-4 text-sm">
                  Comprehensive development services across multiple sectors including climate resilience, food security, poverty alleviation, and more.
                </p>
              </div>
            </div>

            {/* Service Offerings Image */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-4 text-center">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Service Offerings</h3>
                <div className="w-full h-96 bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center">
                  <ImageWithFallback
                    src="/service-offerings.png"
                    alt="USP Consultancy Service Offerings - Monitoring and Evaluation, Research and Analysis, Project Development, Capacity Development"
                    className="w-full h-full object-contain p-4"
                    fallbackSrc="/placeholder-services.png"
                  />
                </div>
                <p className="text-gray-600 mt-4 text-sm">
                  Specialized consulting services including monitoring and evaluation, research and analysis, project development, and capacity building.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accomplishments Graph Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <h2 className="mb-0">Our Accomplishments</h2>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Track our growth and impact through the years - demonstrating our commitment to excellence and sustainable development
            </p>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 rounded-xl p-8 border border-gray-200 shadow-sm">
            {accomplishmentImages.map((img, index) => (
              <div
                key={index}
                className="group relative overflow-hidden  transition-all duration-500 hover:scale-[1.02]"
              >
                <ImageWithFallback
                  src={safeUrl(img.src)}
                  alt={img.alt || 'Accomplishment chart'}
                  className="w-full h-auto rounded-xl transition-transform duration-700 group-hover:scale-105"
                  fallbackSrc="/placeholder-graph.png"
                />
                {/* <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition duration-500"></div> */}
                {/* <p className="mt-4 text-center text-sm text-gray-600 mb-4">
                  {img.caption}
                </p> */}
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 text-sm">
              Visualizing our journey of impact and excellence since {about.foundedYear}
            </p>
          </div>
        </div>
      </section>

      {/* Our Partners Carousel */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="mb-4">Our Partners</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We are proud to collaborate with leading national and international organizations in driving sustainable development.
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative overflow-hidden">
            {/* Gradient Overlays */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10"></div>

            {/* Carousel Track */}
            <div className="flex animate-scroll space-x-4">
              {/* Double the numbers for seamless loop */}
              {[...partners, ...partners].map((partner, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-32 h-20 bg-white rounded-md shadow-sm border border-gray-100 flex items-center justify-center p-3 hover:shadow-md transition-all duration-300 hover:scale-105"
                >
                  <div className="relative w-24 h-14 flex items-center justify-center">
                    <ImageWithFallback
                      src={safeUrl(partner.src)}
                      alt={partner.name || 'Partner logo'}
                      className="w-full h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      fallbackSrc="/placeholder-logo.png"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PageFaq faqs={seo.faqs} />

      {/* Call to Action */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 text-primary-foreground/80">
            Contact us today to discuss your project requirements and discover how we can help achieve your goals.
          </p>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => onPageChange('contact')}
            className="cursor-pointer"
          >
            Contact Our Team
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      <style jsx>{`
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(calc(-50% - 1rem));
      }
    }
    .animate-scroll {
      animation: scroll 40s linear infinite;
      display: flex;
      width: max-content;
    }
    .animate-scroll:hover {
      animation-play-state: paused;
    }

    .about-section {
      padding: 60px 0;
      background-color: #f7f7f7;
    }

    .container {
      max-width: 1100px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .about-content {
      text-align: center;
      max-width: 800px;
      margin: 0 auto;
    }

    .about-content h2 {
      font-size: 28px;
      font-weight: 600;
      margin-bottom: 20px;
    }

    .about-content p {
      font-size: 18px;
      line-height: 1.7;
      color: #555;
      margin-bottom: 20px;
    }

    .last-para {
      margin-bottom: 40px;
    }

    .button-wrap {
      margin-top: 25px;
    }

    .profile-btn {
      display: inline-block;
      padding: 12px 22px;
      background: black;
      color: white;
      text-decoration: none;
      font-size: 16px;
      border-radius: 6px;
      transition: 0.3s ease;
    }

    .profile-btn:hover {
      background: black;
    }
  `}</style>
    </div>
  );
}